import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { identity } from 'lodash';

import { useUser } from '../utils/hooks';
import { AREA_UNITS, LONG_AREA_UNITS } from '../constants/units';
import { asDynamicDecimal } from '../utils/formatters';

export const UNIT_OPTIONS = [
  { label: 'Imperial', value: 'default' },
  { label: 'Metric', value: 'metric' },
];

const initialState = {
  units: UNIT_OPTIONS[0].value,
};

const DEFAULT_UNIT_PRECISION = 4;

export const UnitContext = createContext(initialState);

export const UnitContextProvider = ({ children }) => {
  const [units, setUnits] = useState('default');
  const { defaultUnitSystem } = useUser();
  useEffect(() => {
    // Only set the default if no existing value is already set:
    setUnits((current) => current || defaultUnitSystem);
  }, [defaultUnitSystem]);

  const value = useMemo(
    () => ({
      units,
      setUnits,
    }),
    [units],
  );

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
};

export const useUnitContext = () => useContext(UnitContext);

/* Below are hooks that make use of the the UnitContext. */

const unitFromSpec = (unitSpec, units, unitPrecision = null) => {
  const { display, displayCsv, multiplier = 1 } = unitSpec[units || 'default'];
  const precisionFactor = 10 ** (unitPrecision ?? DEFAULT_UNIT_PRECISION);
  return {
    displayUnit: display,
    displayCsv: displayCsv || display,
    transformToUnit: (v) =>
      // Prevent automatic conversion of undefined, null and empty string to 0
      // Prevent automatic conversion of non-numbers to NaN
      (v && Number(v)) || v === 0
        ? // Round to 4 decimal places to avoid floating point errors from unit conversion.
          // Uses Math.round instead of toFixed to avoid string conversion, because the numeric
          // value is needed for conditional formatting. Since this is generally used for display
          // or as the initial value in an input, it shouldn't affect the stored value.
          Math.round(v * precisionFactor * multiplier) / precisionFactor
        : v,
    transformToDefault: (v) =>
      (v && Number(v)) || v === 0 ? v / multiplier : v,
  };
};

/* Takes a single unit spec and returns the unit object for the system that is
 * specified by the query param.
 *
 * Example
 * input: DISTANCE_UNITS, units=metric
 * output: {
    displayUnit: 'm',
    transformToUnit, // function to convert from default unit to the display unit
    transformToDefault, // function to convert from display unit to the default unit
  }
*/
export const useUnits = (unitSpec, unitPrecision = null) => {
  const { units } = useUnitContext();
  return useMemo(() => {
    if (!unitSpec) return {};
    return unitFromSpec(unitSpec, units, unitPrecision);
  }, [unitSpec, units, unitPrecision]);
};

/* Takes an object that maps from default unit string (eg. 'lbs') to a unit spec.
* Returns an object that maps from default unit string to the unit object for the
* current URL query param. Use for easy lookup by default unit string when there are
* multiple possible units.
*
* Example *
  input: YIELD_UNITS_BY_DEFAULT, units=metric
  return value:
  {
    // Default unit string
    lbs: {
      // Metric unit object
      displayUnit: 'kg',
      transformToUnit, // function to convert from default unit to the display unit
      transformToDefault, // function to convert from display unit to the default unit
    },
    bft: {
      displayUnit: 'm3',
      transformToUnit,
      transformToDefault,
    }
  }
*/
export const useUnitsMap = (unitSpecMap) => {
  const { units } = useUnitContext();
  return useMemo(() => {
    return Object.entries(unitSpecMap).reduce(
      (acc, [key, unitSpec]) => ({
        ...acc,
        [key]: unitFromSpec(unitSpec, units),
      }),
      {},
    );
  }, [units, unitSpecMap]);
};

// Returns formatWithUnit and parseWithUnit functions that apply the correct
// unit transform and format/parse functions.
export const useFormatAndParseWithUnit = ({
  format = identity,
  parse = identity,
  transformToDefault = identity,
  transformToUnit = identity,
}) => {
  const parseWithUnit = useCallback(
    // Parse the value to a number then convert to default units
    (value) => transformToDefault(parse(value)),
    [parse, transformToDefault],
  );

  const formatWithUnit = useCallback(
    // Convert the value to the display unit then format
    (value) => format(transformToUnit(value)),
    [format, transformToUnit],
  );
  return { parseWithUnit, formatWithUnit };
};

// Helper to format area units as a dynamic decimal
export const useAreaUnits = () => {
  const { displayUnit: shortUnitName, transformToUnit = identity } = useUnits(
    AREA_UNITS,
  );

  const { displayUnit: longUnitName } = useUnits(LONG_AREA_UNITS);

  const areaFormatter = (area) => asDynamicDecimal(transformToUnit(area));

  return { areaFormatter, shortUnitName, longUnitName };
};
